import React, { useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { ImagePlus } from 'lucide-react';

export const inputCls = 'w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:outline-hidden focus:border-civix-purple/60';
export const btnPrimary = 'rounded-lg bg-civix-purple px-4 py-2 text-xs font-bold tracking-wide text-white transition hover:bg-civix-purple/90 disabled:cursor-not-allowed disabled:opacity-40';
export const btnGhost = 'rounded-lg border border-white/15 px-4 py-2 text-xs font-bold tracking-wide text-white/80 transition hover:bg-white/5 disabled:cursor-not-allowed disabled:opacity-40';

const OK_TYPES = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'];
export const MAX_BYTES = 5 * 1024 * 1024;

/** Labelled text input / textarea. */
export function TextField({ label, value, onChange, multiline = false, rows = 4, error, hint, required, testId, ...rest }) {
  const Tag = multiline ? 'textarea' : 'input';
  return (
    <label className="block">
      <span className="mb-1 block text-[11px] font-semibold uppercase tracking-wider text-white/50">
        {label}{required && ' *'}
      </span>
      <Tag
        className={`${inputCls} ${error ? 'border-rose-500/60' : ''}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testId}
        {...(multiline ? { rows } : {})}
        {...rest}
      />
      {error && <span className="mt-1 block text-xs text-rose-400" role="alert">{error}</span>}
      {!error && hint && <span className="mt-1 block text-xs text-white/40">{hint}</span>}
    </label>
  );
}

/** Check type + size before spending an upload (`pdf: true` also allows PDFs, for license certificates). @returns {string} problem or ''. */
export function fileProblem(file, { pdf = false } = {}) {
  if (!file) return 'No file selected.';
  if (!OK_TYPES.includes(file.type) && !(pdf && file.type === 'application/pdf')) return pdf ? 'Use a PNG, JPG, WEBP, GIF or PDF file.' : 'Use a PNG, JPG, WEBP or GIF image.';
  if (file.size > MAX_BYTES) return 'Image is larger than 5MB.';
  return '';
}

/**
 * Click-or-drop image uploader.
 * @param {{label: string, onFile: (f: File) => (void|Promise<any>), busy?: boolean, testId?: string}} props
 */
export function ImageDrop({ label, onFile, busy = false, testId }) {
  const ref = useRef(null);
  const [over, setOver] = useState(false);

  function take(file) {
    const problem = fileProblem(file);
    if (problem) { toast.error(problem); return; }
    onFile(file);
  }

  return (
    <div>
      <button
        type="button"
        disabled={busy}
        data-testid={testId}
        onClick={() => ref.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setOver(true); }}
        onDragLeave={() => setOver(false)}
        onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files?.[0]); }}
        className={`flex w-full items-center justify-center gap-2 rounded-lg border px-3 py-2.5 text-xs font-bold uppercase tracking-wide transition ${over ? 'border-civix-purple bg-civix-purple/20' : 'border-civix-purple/50 hover:bg-civix-purple/10'} text-civix-purpleSoft disabled:opacity-50`}
      >
        <ImagePlus className="h-4 w-4" /> {busy ? 'Uploading…' : label}
      </button>
      <input ref={ref} type="file" accept={OK_TYPES.join(',')} className="hidden" onChange={(e) => { take(e.target.files?.[0]); e.target.value = ''; }} />
      <p className="mt-1 text-[11px] text-white/40">Or drag and drop an image file here</p>
    </div>
  );
}
